import type { FunctionComponent, ReactNode } from 'react';
import { useEffect, useRef, useState } from 'react';
import { Box, Drawer, LoadingOverlay } from '@mantine/core';
import { useMediaQuery } from '@mantine/hooks';

import { useEnhancePhoto } from 'api/photos/useEnhancePhoto';
import { trackMenuPhotoSaved } from 'helpers/menuAnalytics';
import { useTranslation } from 'i18n';
import { breakpoints } from 'styles/theme.variables';
import { AddPhotoContent } from './AddPhotoContent';
import { EditPhotoContent } from './EditPhotoContent';
import type { CropState } from './EditPhotoContent';
import { PreviewPhotoContent } from './PreviewPhotoContent';
import { SuccessPhotoContent } from './SuccessPhotoContent';

const NS = 'menuPhotosPhotoDrawer';
const MIN_WIDTH = 640;
const MIN_HEIGHT = 480;

const checkImageDimensions = (imageUrl: string): Promise<{ width: number; height: number }> =>
  new Promise((resolve, reject) => {
    const img = new window.Image();
    img.onload = () => resolve({ width: img.width, height: img.height });
    img.onerror = () => reject(new Error('Failed to load image'));
    img.src = imageUrl;
  });

type DrawerMode = 'add' | 'edit' | 'preview' | 'success';

export type PhotoDrawerProps = {
  opened: boolean;
  onClose: () => void;
  itemName: string;
  companyId?: string;
  menuId?: string;
  itemId?: string;
  activeIntegrationNames?: Set<string>;
  onUpload?: (file: File) => Promise<void>;
  onSave?: (cropState: CropState, imageUrl: string, enhancedAt: Date | null) => Promise<void>;
};

export const PhotoDrawer: FunctionComponent<PhotoDrawerProps> = ({
  opened,
  onClose,
  itemName,
  companyId,
  menuId,
  itemId,
  activeIntegrationNames,
  onUpload,
  onSave,
}) => {
  const { t } = useTranslation(NS);
  const isMobile = useMediaQuery(`(max-width: ${breakpoints.maxMobile})`);
  const { enhancePhoto } = useEnhancePhoto(companyId);
  const [mode, setMode] = useState<DrawerMode>('add');
  const [cropSnapshot, setCropSnapshot] = useState<CropState | null>(null);
  const [blobUrl, setBlobUrl] = useState<string | null>(null);
  const [enhancedUrl, setEnhancedUrl] = useState<string | null>(null);
  // Ref mirrors state so handleSave reads the latest value without stale closures.
  // URL and timestamp are one object so they can never be reset out of step.
  const enhancedDetailsRef = useRef<{ enhancedAt: Date; url: string } | null>(null);
  const [originalFilename, setOriginalFilename] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  // Always reset to upload view on close. Re-cropping existing CDN images is not supported
  // without a backend crop endpoint (CORS prevents canvas operations on cross-origin images).
  useEffect(() => {
    if (!opened) {
      setMode('add');
      setCropSnapshot(null);
      enhancedDetailsRef.current = null;
      setEnhancedUrl(null);
      setSaveError(null);
      setOriginalFilename(null);
      setBlobUrl(prev => {
        if (prev) URL.revokeObjectURL(prev);
        return null;
      });
    }
  }, [opened]);

  const handlePreview = (state: CropState) => {
    setCropSnapshot(state);
    setSaveError(null);
    setMode('preview');
  };

  const handleSave = async () => {
    const imageUrl = enhancedDetailsRef.current?.url ?? blobUrl;
    if (!cropSnapshot || !imageUrl) return;
    setIsSaving(true);
    setSaveError(null);
    try {
      const { width, height } = await checkImageDimensions(imageUrl);
      if (width < MIN_WIDTH || height < MIN_HEIGHT) {
        setSaveError(t('PhotoDrawer.dimensionSaveError'));
        return;
      }
      await onSave?.(cropSnapshot, imageUrl, enhancedDetailsRef.current?.enhancedAt ?? null);
      trackMenuPhotoSaved({ menuId, itemId, usedEnhancedImage: !!enhancedDetailsRef.current });
      setMode('success');
    } catch (err) {
      setSaveError((err as Error)?.message ?? t('PreviewPhotoContent.saveError'));
    } finally {
      setIsSaving(false);
    }
  };

  const titles: Record<DrawerMode, string> = {
    add: t('PhotoDrawer.addPhotoTitle'),
    edit: t('PhotoDrawer.editPhotoTitle'),
    preview: t('PhotoDrawer.previewPhotoTitle'),
    success: t('PhotoDrawer.successPhotoTitle'),
  };

  // Capture a blob URL when the user selects a file. This same-origin blob is used
  // for canvas crop operations, avoiding CDN CORS restrictions.
  const handleUploadWithCapture = onUpload
    ? async (file: File) => {
        const url = URL.createObjectURL(file);
        setBlobUrl(prev => {
          if (prev) URL.revokeObjectURL(prev);
          return url;
        });
        setOriginalFilename(file.name);
        await onUpload(file);
      }
    : undefined;

  const handleSuccessEdit = () => setMode('edit');

  const handleSuccessReplace = () => {
    setBlobUrl(prev => {
      if (prev) URL.revokeObjectURL(prev);
      return null;
    });
    setCropSnapshot(null);
    enhancedDetailsRef.current = null;
    setEnhancedUrl(null);
    setMode('add');
  };

  let content: ReactNode = null;
  if (mode === 'add') {
    content = (
      <AddPhotoContent
        opened={opened}
        itemName={itemName}
        onUpload={handleUploadWithCapture}
        onSuccess={() => setMode('edit')}
      />
    );
  } else if (mode === 'edit' && blobUrl) {
    content = (
      <EditPhotoContent
        imageUrl={blobUrl}
        menuId={menuId}
        itemId={itemId}
        companyId={companyId}
        onEnhance={async () => {
          const url = await enhancePhoto({ imageUrl: blobUrl });
          enhancedDetailsRef.current = { enhancedAt: new Date(), url };
          setEnhancedUrl(url);
          return url;
        }}
        onEnhanceReset={() => {
          enhancedDetailsRef.current = null;
          setEnhancedUrl(null);
        }}
        onReplace={() => {
          setBlobUrl(prev => {
            if (prev) URL.revokeObjectURL(prev);
            return null;
          });
          setCropSnapshot(null);
          enhancedDetailsRef.current = null;
          setEnhancedUrl(null);
          setMode('add');
        }}
        onPreview={handlePreview}
        initialState={cropSnapshot ?? undefined}
        initialEnhancedUrl={enhancedUrl}
      />
    );
  } else if (mode === 'preview' && blobUrl && cropSnapshot) {
    content = (
      <PreviewPhotoContent
        imageUrl={enhancedUrl ?? blobUrl}
        cropState={cropSnapshot}
        isSaving={isSaving}
        saveError={saveError}
        activeIntegrationNames={activeIntegrationNames}
        onSave={() => {
          void handleSave();
        }}
        onBack={() => setMode('edit')}
      />
    );
  } else if (mode === 'success' && blobUrl && cropSnapshot) {
    content = (
      <SuccessPhotoContent
        imageUrl={enhancedUrl ?? blobUrl}
        originalImageUrl={blobUrl}
        cropState={cropSnapshot}
        filename={originalFilename ?? undefined}
        activeIntegrationNames={activeIntegrationNames}
        onDone={onClose}
        onEdit={handleSuccessEdit}
        onReplace={handleSuccessReplace}
      />
    );
  }

  return (
    <Drawer
      opened={opened}
      onClose={onClose}
      title={titles[mode]}
      size={isMobile ? 'sm' : 'lg'}
      variant={mode !== 'add' ? 'withDock' : undefined}
      position="right"
    >
      <LoadingOverlay visible={isSaving} />
      <Box h="100%">{content}</Box>
    </Drawer>
  );
};
